import React, { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data/content";

const Wordmark = ({ onClick, light }) => (
  <Link
    to="/"
    onClick={onClick}
    data-testid="nav-logo"
    className="group inline-flex items-center"
    aria-label="Yadid Advisory — Home"
  >
    <img
      src={light ? "/logo-white.png" : "/logo-navy.png"}
      alt="Yadid Advisory"
      className="h-14 md:h-[68px] w-auto transition-opacity duration-500 group-hover:opacity-80"
    />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Light (ivory) treatment when over a dark hero and not yet scrolled
  const light = pathname.startsWith("/journeys") && !scrolled;

  return (
    <>
      <header
        data-testid="site-header"
        className={
          "fixed top-0 inset-x-0 z-50 transition-all duration-700 " +
          (scrolled
            ? "bg-ivory/90 backdrop-blur-xl border-b border-stone/70 py-4"
            : "bg-transparent py-6")
        }
      >
        <div className="yadid-container flex items-center gap-7">
          <Wordmark light={light} />

          <nav className="hidden lg:flex flex-1 items-center justify-center gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  "whitespace-nowrap text-[0.8rem] tracking-[0.08em] font-medium transition-colors duration-400 link-underline " +
                  (light
                    ? isActive
                      ? "text-ivory"
                      : "text-ivory/70 hover:text-ivory"
                    : isActive
                    ? "text-ink"
                    : "text-ink/60 hover:text-ink")
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-6">
            <span
              aria-hidden="true"
              className={"h-5 w-px " + (light ? "bg-ivory/20" : "bg-ink/15")}
            />
            <Link
              to="/contact"
              data-testid="nav-cta"
              className={
                "inline-flex items-center whitespace-nowrap px-6 py-3 text-[0.72rem] tracking-[0.16em] uppercase font-semibold transition-all duration-500 " +
                (light
                  ? "border border-brass text-brass hover:bg-brass hover:text-ink"
                  : "bg-ink text-ivory hover:bg-[#16294c]")
              }
            >
              Start a Conversation
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={"lg:hidden ml-auto p-1 transition-colors duration-500 " + (light ? "text-ivory" : "text-ink")}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu strokeWidth={1.5} size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-ink text-ivory"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="yadid-container flex items-center justify-between py-6">
              <img src="/logo-white.png" alt="Yadid Advisory" className="h-11 w-auto" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="text-ivory"
              >
                <X strokeWidth={1.5} size={26} />
              </button>
            </div>
            <nav className="yadid-container mt-8 flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-link-${l.to.replace("/", "")}`}
                    className="block font-serif text-4xl py-3 border-b border-white/10 text-ivory"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center justify-center border border-brass text-brass px-8 py-4 text-[0.78rem] tracking-[0.16em] uppercase font-semibold"
              >
                Start a Conversation
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
